/**
 * PurchaseOrderDrawer Component
 * Create/Edit/View Purchase Order form
 * BuilderBel Construction Project Management System
 *
 * PR 1.8: Fixed issues with form components, validation, and field names
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  MapPin,
  Package,
  Plus,
  ShoppingCart,
  Trash2,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetCostCodesQuery } from '../../services/costCodeApi';
import {
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
} from '../../services/purchaseOrderApi';
import { useGetVendorsQuery } from '../../services/vendorApi';
import type { CostCode } from '../../types/costCode';
import type {
  CreatePurchaseOrderRequest,
  PurchaseOrder,
  PurchaseOrderPriority,
  PurchaseOrderShipTo,
  PurchaseOrderType,
  UpdatePurchaseOrderRequest,
} from '../../types/purchaseOrder';
import type { Vendor } from '../../types/vendor';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  Input,
  NumberInput,
  PhoneInput,
  SearchableDropdown,
  TextArea,
} from '../forms';

// Props interface
export interface PurchaseOrderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder?: PurchaseOrder | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// Line item form data - using unitCost to match backend
interface LineItemFormData {
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  costCode?: string;
  notes?: string;
}

// Form data type
interface PurchaseOrderFormData {
  title: string;
  description: string;
  type: PurchaseOrderType;
  vendorId: string;
  vendorName: string;
  vendorContactName: string;
  vendorEmail: string;
  vendorPhone: string;
  vendorAddress: string;
  priority: PurchaseOrderPriority;
  issueDate: string;
  dueDate: string;
  deliveryDate: string;
  requiredDate: string;
  paymentTerms: string;
  retentionPercent: number;
  deliveryTerms: string;
  terms: string;
  notes: string;
  internalNotes: string;
  shippingCost: number;
  taxRate: number;
  currency: string;
  lineItems: LineItemFormData[];
  // Ship To fields
  shipToName: string;
  shipToAddress: string;
  shipToCity: string;
  shipToState: string;
  shipToZipCode: string;
  shipToCountry: string;
  shipToPhone: string;
  shipToInstructions: string;
}

// Validation schema - matching backend requirements
const lineItemSchema = Yup.object({
  description: Yup.string().required('Description is required'),
  quantity: Yup.number().required('Quantity is required').min(1, 'Quantity must be at least 1'),
  unit: Yup.string().required('Unit is required'),
  unitCost: Yup.number().required('Unit cost is required').min(0, 'Unit cost must be positive'),
  costCode: Yup.string().optional(),
  notes: Yup.string().optional(),
});

const purchaseOrderSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  description: Yup.string().optional(),
  type: Yup.string()
    .oneOf(['material', 'equipment', 'subcontract', 'service'])
    .required('Type is required'),
  vendorId: Yup.string().optional(),
  vendorName: Yup.string().required('Vendor name is required'),
  vendorContactName: Yup.string().optional(),
  vendorEmail: Yup.string().email('Invalid email').optional(),
  vendorPhone: Yup.string().optional(),
  vendorAddress: Yup.string().optional(),
  priority: Yup.string()
    .oneOf(['low', 'medium', 'high', 'urgent'])
    .required('Priority is required'),
  issueDate: Yup.string().required('Issue date is required'),
  dueDate: Yup.string().optional(),
  deliveryDate: Yup.string().optional(),
  requiredDate: Yup.string().optional(),
  paymentTerms: Yup.string().optional(),
  retentionPercent: Yup.number().min(0).max(100, 'Retention cannot exceed 100%').optional(),
  deliveryTerms: Yup.string().optional(),
  terms: Yup.string().optional(),
  notes: Yup.string().optional(),
  internalNotes: Yup.string().optional(),
  shippingCost: Yup.number().min(0, 'Shipping cost must be positive').optional(),
  taxRate: Yup.number().min(0).max(100, 'Tax rate cannot exceed 100%').optional(),
  currency: Yup.string().required('Currency is required'),
  lineItems: Yup.array().of(lineItemSchema).min(1, 'At least one line item is required'),
  // Ship To fields (all optional)
  shipToName: Yup.string().optional(),
  shipToAddress: Yup.string().optional(),
  shipToCity: Yup.string().optional(),
  shipToState: Yup.string().optional(),
  shipToZipCode: Yup.string().optional(),
  shipToCountry: Yup.string().optional(),
  shipToPhone: Yup.string().optional(),
  shipToInstructions: Yup.string().optional(),
});

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// PO Type options
const PO_TYPE_OPTIONS = [
  { value: 'material', label: 'Material' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'subcontract', label: 'Subcontract' },
  { value: 'service', label: 'Service' },
];

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
];

// Unit options
const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'unit', label: 'Unit' },
  { value: 'box', label: 'Box' },
  { value: 'pack', label: 'Pack' },
  { value: 'kg', label: 'Kilogram' },
  { value: 'lb', label: 'Pound' },
  { value: 'ton', label: 'Ton' },
  { value: 'm', label: 'Meter' },
  { value: 'ft', label: 'Feet' },
  { value: 'sqm', label: 'Square Meter' },
  { value: 'sqft', label: 'Square Feet' },
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'lot', label: 'Lot' },
  { value: 'ls', label: 'Lump Sum' },
];

// Payment terms options
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Select payment terms' },
  { value: 'Net 15', label: 'Net 15' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 45', label: 'Net 45' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
  { value: '50% Upfront, 50% on Delivery', label: '50% Upfront, 50% on Delivery' },
  { value: 'COD', label: 'Cash on Delivery' },
];

// Get today's date in ISO format
const getTodayISO = (): string => {
  return new Date().toISOString().split('T')[0];
};

const PurchaseOrderDrawer: React.FC<PurchaseOrderDrawerProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const { success, error: showError } = useToast();
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    vendor: true,
    lineItems: true,
    shipTo: false,
    delivery: true,
    financial: true,
    notes: false,
  });

  // API mutations
  const [createPO, { isLoading: isCreating }] = useCreatePurchaseOrderMutation();
  const [updatePO, { isLoading: isUpdating }] = useUpdatePurchaseOrderMutation();
  const isSubmitting = isCreating || isUpdating;

  // Cost codes query
  const { data: costCodesResponse } = useGetCostCodesQuery({ isActive: true, limit: 500 });
  const costCodeOptions = useMemo(() => {
    const costCodes = costCodesResponse?.data?.costCodes || [];
    return costCodes.map((cc: CostCode) => ({
      value: cc._id,
      label: `${cc.code} - ${cc.description}`,
    }));
  }, [costCodesResponse]);

  // Vendors query
  const { data: vendorsResponse } = useGetVendorsQuery({ status: 'active', limit: 500 });
  const vendors = useMemo(() => vendorsResponse?.data?.vendors || [], [vendorsResponse]);
  const vendorOptions = useMemo(() => {
    return vendors.map((v: Vendor) => ({
      value: v._id,
      label: `${v.code} - ${v.name}`,
    }));
  }, [vendors]);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PurchaseOrderFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(purchaseOrderSchema) as any,
    defaultValues: {
      title: '',
      description: '',
      type: 'material',
      vendorId: '',
      vendorName: '',
      vendorContactName: '',
      vendorEmail: '',
      vendorPhone: '',
      vendorAddress: '',
      priority: 'medium',
      issueDate: getTodayISO(),
      dueDate: '',
      deliveryDate: '',
      requiredDate: '',
      paymentTerms: '',
      retentionPercent: 0,
      deliveryTerms: '',
      terms: '',
      notes: '',
      internalNotes: '',
      shippingCost: 0,
      taxRate: 0,
      currency: 'USD',
      lineItems: [{ description: '', quantity: 1, unit: 'each', unitCost: 0 }],
      // Ship To
      shipToName: '',
      shipToAddress: '',
      shipToCity: '',
      shipToState: '',
      shipToZipCode: '',
      shipToCountry: 'USA',
      shipToPhone: '',
      shipToInstructions: '',
    },
  });

  // Line items field array
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  // Watch line items for total calculation
  const watchedLineItems = watch('lineItems');
  const watchedShippingCost = watch('shippingCost');
  const watchedTaxRate = watch('taxRate');

  // Calculate totals
  const { subtotal, taxAmount, total } = useMemo(() => {
    const sub =
      watchedLineItems?.reduce((acc, item) => {
        const qty = item?.quantity || 0;
        const cost = item?.unitCost || 0;
        return acc + qty * cost;
      }, 0) || 0;
    const shipping = watchedShippingCost || 0;
    const tax = sub * ((watchedTaxRate || 0) / 100);
    return { subtotal: sub, taxAmount: tax, total: sub + tax + shipping };
  }, [watchedLineItems, watchedShippingCost, watchedTaxRate]);

  // Toggle section
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Reset form when purchaseOrder changes
  useEffect(() => {
    if (purchaseOrder && (isEditMode || isViewMode)) {
      reset({
        title: purchaseOrder.title || '',
        description: purchaseOrder.notes || '', // Backend uses 'notes' for external notes
        type: purchaseOrder.type || 'material',
        vendorId: purchaseOrder.vendorId || '',
        vendorName: purchaseOrder.vendor.name,
        vendorContactName: purchaseOrder.vendor.contactName || '',
        vendorEmail: purchaseOrder.vendor.email || '',
        vendorPhone: purchaseOrder.vendor.phone || '',
        vendorAddress: purchaseOrder.vendor.address || '',
        priority: purchaseOrder.priority || 'medium',
        issueDate: purchaseOrder.issueDate?.split('T')[0] || getTodayISO(),
        dueDate: purchaseOrder.dueDate?.split('T')[0] || '',
        deliveryDate: purchaseOrder.deliveryDate?.split('T')[0] || '',
        requiredDate: purchaseOrder.requiredDate?.split('T')[0] || '',
        paymentTerms: purchaseOrder.paymentTerms || '',
        retentionPercent: purchaseOrder.retentionPercent || 0,
        deliveryTerms: '',
        terms: purchaseOrder.terms || '',
        notes: purchaseOrder.notes || '',
        internalNotes: purchaseOrder.internalNotes || '',
        shippingCost: purchaseOrder.shippingCost || 0,
        taxRate: purchaseOrder.taxRate || 0,
        currency: purchaseOrder.currency || 'USD',
        lineItems: purchaseOrder.lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitCost: item.unitCost,
          costCode: item.costCodeId || '',
          notes: item.notes || '',
        })),
        // Ship To
        shipToName: purchaseOrder.shipTo?.name || '',
        shipToAddress: purchaseOrder.shipTo?.address || '',
        shipToCity: purchaseOrder.shipTo?.city || '',
        shipToState: purchaseOrder.shipTo?.state || '',
        shipToZipCode: purchaseOrder.shipTo?.zipCode || '',
        shipToCountry: purchaseOrder.shipTo?.country || 'USA',
        shipToPhone: purchaseOrder.shipTo?.phone || '',
        shipToInstructions: purchaseOrder.shipTo?.instructions || '',
      });
    } else if (mode === 'create') {
      reset({
        title: '',
        description: '',
        type: 'material',
        vendorId: '',
        vendorName: '',
        vendorContactName: '',
        vendorEmail: '',
        vendorPhone: '',
        vendorAddress: '',
        priority: 'medium',
        issueDate: getTodayISO(),
        dueDate: '',
        deliveryDate: '',
        requiredDate: '',
        paymentTerms: '',
        retentionPercent: 0,
        deliveryTerms: '',
        terms: '',
        notes: '',
        internalNotes: '',
        shippingCost: 0,
        taxRate: 0,
        currency: 'USD',
        lineItems: [{ description: '', quantity: 1, unit: 'each', unitCost: 0 }],
        // Ship To
        shipToName: '',
        shipToAddress: '',
        shipToCity: '',
        shipToState: '',
        shipToZipCode: '',
        shipToCountry: 'USA',
        shipToPhone: '',
        shipToInstructions: '',
      });
    }
  }, [purchaseOrder, mode, reset, isEditMode, isViewMode]);

  // Handle vendor selection - auto-fill vendor details
  const handleVendorSelect = (vendorId: string) => {
    setValue('vendorId', vendorId);
    if (vendorId) {
      const selectedVendor = vendors.find((v: Vendor) => v._id === vendorId);
      if (selectedVendor) {
        setValue('vendorName', selectedVendor.name);
        setValue('vendorContactName', selectedVendor.contact?.name || '');
        setValue('vendorEmail', selectedVendor.contact?.email || '');
        setValue('vendorPhone', selectedVendor.contact?.phone || '');
        // Format address from address object
        const addr = selectedVendor.address;
        const addressParts = [
          addr?.street,
          addr?.city,
          addr?.state,
          addr?.zipCode,
          addr?.country,
        ].filter(Boolean);
        setValue('vendorAddress', addressParts.join(', '));
      }
    } else {
      // Clear vendor fields when deselected
      setValue('vendorName', '');
      setValue('vendorContactName', '');
      setValue('vendorEmail', '');
      setValue('vendorPhone', '');
      setValue('vendorAddress', '');
    }
  };

  // Handle form submission
  const handleFormSubmit = async (data: PurchaseOrderFormData) => {
    if (!projectId) {
      showError('No project selected');
      return;
    }

    try {
      if (mode === 'create') {
        // Build shipTo only if any field is filled
        const shipTo: PurchaseOrderShipTo | undefined =
          data.shipToName || data.shipToAddress
            ? {
                name: data.shipToName,
                address: data.shipToAddress,
                city: data.shipToCity,
                state: data.shipToState,
                zipCode: data.shipToZipCode,
                country: data.shipToCountry || 'USA',
                phone: data.shipToPhone || undefined,
                instructions: data.shipToInstructions || undefined,
              }
            : undefined;

        const createData: CreatePurchaseOrderRequest = {
          projectId,
          title: data.title,
          type: data.type,
          priority: data.priority,
          vendorId: data.vendorId || undefined,
          vendor: {
            name: data.vendorName,
            contactName: data.vendorContactName || undefined,
            email: data.vendorEmail || undefined,
            phone: data.vendorPhone || undefined,
            address: data.vendorAddress || undefined,
          },
          shipTo,
          lineItems: data.lineItems.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            costCodeId: item.costCode || undefined,
            notes: item.notes || undefined,
          })),
          issueDate: data.issueDate,
          dueDate: data.dueDate || undefined,
          deliveryDate: data.deliveryDate || undefined,
          requiredDate: data.requiredDate || undefined,
          paymentTerms: data.paymentTerms || undefined,
          retentionPercent: data.retentionPercent || undefined,
          terms: data.terms || undefined,
          notes: data.notes || undefined,
          internalNotes: data.internalNotes || undefined,
          shippingCost: data.shippingCost || undefined,
          taxRate: data.taxRate || undefined,
          currency: data.currency,
        };

        await createPO({ projectId, data: createData }).unwrap();
        success('Purchase order created successfully');
      } else if (mode === 'edit' && purchaseOrder) {
        // Build shipTo only if any field is filled
        const shipTo: Partial<PurchaseOrderShipTo> | null =
          data.shipToName || data.shipToAddress
            ? {
                name: data.shipToName,
                address: data.shipToAddress,
                city: data.shipToCity,
                state: data.shipToState,
                zipCode: data.shipToZipCode,
                country: data.shipToCountry || 'USA',
                phone: data.shipToPhone || undefined,
                instructions: data.shipToInstructions || undefined,
              }
            : null;

        const updateData: UpdatePurchaseOrderRequest = {
          title: data.title,
          type: data.type,
          priority: data.priority,
          vendorId: data.vendorId || undefined,
          vendor: {
            name: data.vendorName,
            contactName: data.vendorContactName || undefined,
            email: data.vendorEmail || undefined,
            phone: data.vendorPhone || undefined,
            address: data.vendorAddress || undefined,
          },
          shipTo,
          issueDate: data.issueDate,
          dueDate: data.dueDate || undefined,
          deliveryDate: data.deliveryDate || undefined,
          requiredDate: data.requiredDate || undefined,
          lineItems: data.lineItems.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            costCodeId: item.costCode || undefined,
            notes: item.notes || undefined,
          })),
          paymentTerms: data.paymentTerms || undefined,
          retentionPercent: data.retentionPercent || undefined,
          terms: data.terms || undefined,
          notes: data.notes || undefined,
          internalNotes: data.internalNotes || undefined,
          shippingCost: data.shippingCost || undefined,
          taxRate: data.taxRate || undefined,
          currency: data.currency,
        };

        await updatePO({
          projectId,
          purchaseOrderId: purchaseOrder._id,
          data: updateData,
        }).unwrap();
        success('Purchase order updated successfully');
      }

      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as {
        data?: { message?: string; errors?: Array<{ field: string; message: string }> };
        message?: string;
      };
      if (error.data?.errors) {
        const errorMessages = error.data.errors.map(e => `${e.field}: ${e.message}`).join(', ');
        showError(errorMessages);
      } else {
        showError(error.data?.message || error.message || 'Failed to save purchase order');
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addLineItem = () => {
    append({ description: '', quantity: 1, unit: 'each', unitCost: 0 });
  };

  const removeLineItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: watch('currency') || 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Drawer - Large size for multiple sections */}
      <div className="fixed top-6 right-6 bottom-6 left-6 z-[100] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <ShoppingCart className="h-5 w-5 text-brand-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create'
                    ? 'Create Purchase Order'
                    : mode === 'edit'
                      ? 'Edit Purchase Order'
                      : `PO: ${purchaseOrder?.poNumber}`}
                </h2>
                {purchaseOrder && (
                  <p className="text-sm text-gray-500">{purchaseOrder.vendor.name}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Basic Information Section */}
              <section className="space-y-4">
                <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Basic Information
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Title"
                        value={field.value}
                        onChange={e => field.onChange(e.target.value)}
                        error={errors.title?.message}
                        disabled={isViewMode}
                        placeholder="Enter PO title"
                        required
                      />
                    )}
                  />

                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="PO Type"
                        options={PO_TYPE_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.type?.message}
                        disabled={isViewMode}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Priority"
                        options={PRIORITY_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.priority?.message}
                        disabled={isViewMode}
                        required
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="issueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Issue Date"
                        value={field.value}
                        onChange={date => field.onChange(date || '')}
                        error={errors.issueDate?.message}
                        disabled={isViewMode}
                        required
                        placeholder="Select issue date"
                      />
                    )}
                  />

                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Currency"
                        options={CURRENCY_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.currency?.message}
                        disabled={isViewMode}
                        required
                      />
                    )}
                  />
                </div>

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Description"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.description?.message}
                      disabled={isViewMode}
                      placeholder="Enter purchase order description"
                      rows={2}
                    />
                  )}
                />
              </section>

              {/* Vendor Information Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('vendor')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Vendor Information
                    </h3>
                  </div>
                  {expandedSections.vendor ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.vendor && (
                  <div className="space-y-4 pt-2">
                    {/* Vendor Selection Dropdown */}
                    <Controller
                      name="vendorId"
                      control={control}
                      render={({ field }) => (
                        <SearchableDropdown
                          label="Select Vendor"
                          options={[
                            { value: '', label: 'Select a vendor or enter manually...' },
                            ...vendorOptions,
                          ]}
                          value={field.value}
                          onChange={value => handleVendorSelect(value)}
                          disabled={isViewMode}
                          placeholder="Search vendors..."
                        />
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Controller
                        name="vendorName"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Vendor Name"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.vendorName?.message}
                            disabled={isViewMode}
                            required
                            placeholder="Enter vendor name"
                          />
                        )}
                      />

                      <Controller
                        name="vendorContactName"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Contact Name"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.vendorContactName?.message}
                            disabled={isViewMode}
                            placeholder="Enter contact person"
                          />
                        )}
                      />

                      <Controller
                        name="vendorEmail"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Email"
                            type="email"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.vendorEmail?.message}
                            disabled={isViewMode}
                            placeholder="vendor@example.com"
                          />
                        )}
                      />

                      <Controller
                        name="vendorPhone"
                        control={control}
                        render={({ field }) => (
                          <PhoneInput
                            label="Phone"
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            error={errors.vendorPhone?.message}
                            disabled={isViewMode}
                          />
                        )}
                      />
                    </div>

                    <Controller
                      name="vendorAddress"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Address"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.vendorAddress?.message}
                          disabled={isViewMode}
                          placeholder="Enter vendor address"
                          rows={2}
                        />
                      )}
                    />
                  </div>
                )}
              </section>

              {/* Line Items Section */}
              <section className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Line Items
                    </h3>
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="btn btn-secondary btn-sm"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Item
                    </button>
                  )}
                </div>

                {/* Line Items Errors */}
                {errors.lineItems &&
                  typeof errors.lineItems === 'object' &&
                  'message' in errors.lineItems && (
                    <div className="flex items-center space-x-2 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{errors.lineItems.message}</span>
                    </div>
                  )}

                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">Item {index + 1}</h4>
                        {!isViewMode && fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                          <Controller
                            name={`lineItems.${index}.description`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Input
                                label="Description"
                                value={inputField.value}
                                onChange={e => inputField.onChange(e.target.value)}
                                error={errors.lineItems?.[index]?.description?.message}
                                disabled={isViewMode}
                                required
                                placeholder="Item description"
                              />
                            )}
                          />
                        </div>

                        <Controller
                          name={`lineItems.${index}.quantity`}
                          control={control}
                          render={({ field: inputField }) => (
                            <NumberInput
                              label="Quantity"
                              value={inputField.value}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.quantity?.message}
                              disabled={isViewMode}
                              required
                              min={1}
                            />
                          )}
                        />

                        <Controller
                          name={`lineItems.${index}.unit`}
                          control={control}
                          render={({ field: inputField }) => (
                            <Dropdown
                              label="Unit"
                              options={UNIT_OPTIONS}
                              value={inputField.value}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.unit?.message}
                              disabled={isViewMode}
                              required
                            />
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Controller
                          name={`lineItems.${index}.unitCost`}
                          control={control}
                          render={({ field: inputField }) => (
                            <CurrencyInput
                              label="Unit Cost"
                              value={inputField.value}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.unitCost?.message}
                              disabled={isViewMode}
                              required
                            />
                          )}
                        />

                        <Controller
                          name={`lineItems.${index}.costCode`}
                          control={control}
                          render={({ field: inputField }) => (
                            <SearchableDropdown
                              label="Cost Code"
                              options={costCodeOptions}
                              value={inputField.value || ''}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.costCode?.message}
                              disabled={isViewMode}
                              placeholder="Select cost code..."
                            />
                          )}
                        />

                        <div className="md:col-span-2">
                          <Controller
                            name={`lineItems.${index}.notes`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Input
                                label="Notes"
                                value={inputField.value || ''}
                                onChange={e => inputField.onChange(e.target.value)}
                                error={errors.lineItems?.[index]?.notes?.message}
                                disabled={isViewMode}
                                placeholder="Line item notes"
                              />
                            )}
                          />
                        </div>
                      </div>

                      {/* Line Item Total */}
                      <div className="text-right">
                        <span className="text-sm font-medium text-gray-700">
                          Line Total:{' '}
                          <span className="text-gray-900">
                            {formatCurrency(
                              (watchedLineItems?.[index]?.quantity || 0) *
                                (watchedLineItems?.[index]?.unitCost || 0)
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Delivery & Terms Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('delivery')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Delivery & Terms
                    </h3>
                  </div>
                  {expandedSections.delivery ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.delivery && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Controller
                        name="dueDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Due Date"
                            value={field.value}
                            onChange={date => field.onChange(date || '')}
                            error={errors.dueDate?.message}
                            disabled={isViewMode}
                            placeholder="Select due date"
                            minDate={watch('issueDate')}
                          />
                        )}
                      />

                      <Controller
                        name="deliveryDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Expected Delivery"
                            value={field.value}
                            onChange={date => field.onChange(date || '')}
                            error={errors.deliveryDate?.message}
                            disabled={isViewMode}
                            placeholder="Select delivery date"
                          />
                        )}
                      />

                      <Controller
                        name="requiredDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Required By"
                            value={field.value}
                            onChange={date => field.onChange(date || '')}
                            error={errors.requiredDate?.message}
                            disabled={isViewMode}
                            placeholder="Required by date"
                          />
                        )}
                      />

                      <Controller
                        name="paymentTerms"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            label="Payment Terms"
                            options={PAYMENT_TERMS_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.paymentTerms?.message}
                            disabled={isViewMode}
                          />
                        )}
                      />
                    </div>

                    <Controller
                      name="deliveryTerms"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Delivery Terms"
                          value={field.value}
                          onChange={e => field.onChange(e.target.value)}
                          error={errors.deliveryTerms?.message}
                          disabled={isViewMode}
                          placeholder="e.g., FOB, CIF"
                        />
                      )}
                    />

                    <Controller
                      name="terms"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Terms & Conditions"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.terms?.message}
                          disabled={isViewMode}
                          placeholder="Enter terms and conditions"
                          rows={3}
                        />
                      )}
                    />
                  </div>
                )}
              </section>

              {/* Ship To Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('shipTo')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Ship To Address
                    </h3>
                  </div>
                  {expandedSections.shipTo ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.shipTo && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Controller
                        name="shipToName"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Recipient Name"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.shipToName?.message}
                            disabled={isViewMode}
                            placeholder="Name or company"
                          />
                        )}
                      />

                      <Controller
                        name="shipToPhone"
                        control={control}
                        render={({ field }) => (
                          <PhoneInput
                            label="Phone"
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            error={errors.shipToPhone?.message}
                            disabled={isViewMode}
                          />
                        )}
                      />
                    </div>

                    <Controller
                      name="shipToAddress"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Street Address"
                          value={field.value}
                          onChange={e => field.onChange(e.target.value)}
                          error={errors.shipToAddress?.message}
                          disabled={isViewMode}
                          placeholder="Street address or PO Box"
                        />
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Controller
                        name="shipToCity"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="City"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.shipToCity?.message}
                            disabled={isViewMode}
                            placeholder="City"
                          />
                        )}
                      />

                      <Controller
                        name="shipToState"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="State/Province"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.shipToState?.message}
                            disabled={isViewMode}
                            placeholder="State"
                          />
                        )}
                      />

                      <Controller
                        name="shipToZipCode"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="ZIP/Postal Code"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.shipToZipCode?.message}
                            disabled={isViewMode}
                            placeholder="ZIP code"
                          />
                        )}
                      />

                      <Controller
                        name="shipToCountry"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Country"
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                            error={errors.shipToCountry?.message}
                            disabled={isViewMode}
                            placeholder="Country"
                          />
                        )}
                      />
                    </div>

                    <Controller
                      name="shipToInstructions"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Delivery Instructions"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.shipToInstructions?.message}
                          disabled={isViewMode}
                          placeholder="Special delivery instructions, gate codes, etc."
                          rows={2}
                        />
                      )}
                    />
                  </div>
                )}
              </section>

              {/* Financial Summary Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('financial')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Financial Summary
                    </h3>
                  </div>
                  {expandedSections.financial ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.financial && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Controller
                        name="taxRate"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Tax Rate (%)"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.taxRate?.message}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <Controller
                        name="shippingCost"
                        control={control}
                        render={({ field }) => (
                          <CurrencyInput
                            label="Shipping Cost"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.shippingCost?.message}
                            disabled={isViewMode}
                          />
                        )}
                      />

                      <Controller
                        name="retentionPercent"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Retention (%)"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.retentionPercent?.message}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <div className="flex flex-col justify-end">
                        <label className="block text-xs text-gray-500 mb-1">Subtotal</label>
                        <div className="text-lg font-medium text-gray-900">
                          {formatCurrency(subtotal)}
                        </div>
                      </div>
                    </div>

                    {/* Tax and Totals */}
                    <div className="bg-gray-100 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="text-gray-900">{formatCurrency(subtotal)}</span>
                      </div>
                      {watchedTaxRate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tax ({watchedTaxRate}%):</span>
                          <span className="text-gray-900">{formatCurrency(taxAmount)}</span>
                        </div>
                      )}
                      {watchedShippingCost > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Shipping:</span>
                          <span className="text-gray-900">
                            {formatCurrency(watchedShippingCost)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-semibold border-t border-gray-300 pt-2 mt-2">
                        <span className="text-gray-900">Total:</span>
                        <span className="text-brand-600">{formatCurrency(total)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* Notes Section - Collapsible */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('notes')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Notes
                    </h3>
                  </div>
                  {expandedSections.notes ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.notes && (
                  <div className="space-y-4 pt-2">
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="External Notes"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.notes?.message}
                          disabled={isViewMode}
                          placeholder="Notes visible to vendor"
                          rows={2}
                        />
                      )}
                    />

                    <Controller
                      name="internalNotes"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Internal Notes"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.internalNotes?.message}
                          disabled={isViewMode}
                          placeholder="Internal notes (not visible to vendor)"
                          rows={2}
                          hint="These notes are only visible to your team"
                        />
                      )}
                    />
                  </div>
                )}
              </section>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-4 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex justify-between items-center">
              <button type="button" onClick={handleClose} className="btn btn-secondary">
                {isViewMode ? 'Close' : 'Cancel'}
              </button>

              {!isViewMode && (
                <button
                  type="submit"
                  onClick={handleSubmit(handleFormSubmit)}
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                      </span>
                      {mode === 'create' ? 'Creating...' : 'Saving...'}
                    </>
                  ) : (
                    <>{mode === 'create' ? 'Create Purchase Order' : 'Save Changes'}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default PurchaseOrderDrawer;
